import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useRequiredTenantId} from '@/matic/hooks/useRequiredTenantId';
import {getRelayApi} from '../api/relay-api';
import {relayKeys} from '../query-keys';
import type {CreatePinBody, RelayPin} from '../types/relay-types';

type UpdatePinArgs = {pinId: string; label: string};

export function usePinMutations() {
  const tenantId = useRequiredTenantId();
  const queryClient = useQueryClient();
  const api = getRelayApi();

  const createPin = useMutation({
    mutationFn: async (body: CreatePinBody) => api.createPin(body),
    async onSettled() {
      await queryClient.invalidateQueries({
        queryKey: relayKeys.pins.list(tenantId),
      });
    },
  });

  const updatePin = useMutation({
    mutationFn: async ({pinId, label}: UpdatePinArgs) =>
      api.updatePin(pinId, {label}),
    async onSettled() {
      await queryClient.invalidateQueries({
        queryKey: relayKeys.pins.list(tenantId),
      });
    },
  });

  const deletePin = useMutation({
    mutationFn: async (pinId: string) => api.deletePin(pinId),
    async onMutate(pinId: string) {
      const key = relayKeys.pins.list(tenantId);
      await queryClient.cancelQueries({queryKey: key});
      const previous = queryClient.getQueryData<RelayPin[]>(key);
      queryClient.setQueryData<RelayPin[]>(key, (old) =>
        old ? old.filter((p) => p.id !== pinId) : []
      );
      return {previous};
    },
    onError(_err, _pinId, context) {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(
          relayKeys.pins.list(tenantId),
          context.previous
        );
      }
    },
    async onSettled() {
      await queryClient.invalidateQueries({
        queryKey: relayKeys.pins.list(tenantId),
      });
    },
  });

  return {createPin, updatePin, deletePin};
}
